import type { ISocialApplyOffer } from "@api";
import type { ICandidate } from "@bbj-types/candidate";
import type { ISocialOffer } from "@bbj-types/offer";
import type { ISocialApplyTransition } from "@bbj-types/socialApply";
import type { SocialApplyStatus } from "@bbj-types/socialApply/enum";
import type { ObjectId } from "@bbj-types/utils";
import type { IDateRange } from "@inputs/date/DateRangePicker";
import { useGlobalContext } from "@root/context";
import type { Dispatch, SetStateAction } from "react";
import { BaseDrawerSocialOffer } from "../BaseDrawerSocialOffer";
import { DrawerSocialOfferBody } from "../Body";
import { DrawerSocialOfferHeader } from "../Header";

export interface IStandardDrawerSocialOfferProps<T extends ISocialOffer> {
  open: boolean;
  offer: T;
  onClose: () => void;
  handleRemoveOffer?: (socialOffer: T) => void;
  handleRemoveSocialOfferSpotlight?: (socialOfferSpotlight: T) => void;
  onUpdateOffer?: (
    socialOffer: T & {
      status?: SocialApplyStatus;
      transitions?: ISocialApplyTransition[];
      socialApplyId?: string;
      spotlightDates?: IDateRange;
      spotlightId?: ObjectId;
    }
  ) => void;
  onUpdateSocialApply?: (status: SocialApplyStatus) => void;
  openEditModal?: (socialOffer: T, isDuplication: boolean) => void;
  candidate?: ICandidate;
  isApply?: boolean;
  setSocialApplyOfferFupModal?: Dispatch<SetStateAction<ISocialApplyOffer | null>>;
  anchor?: "left" | "top" | "right" | "bottom";
}

export const StandardDrawerSocialOffer = <
  T extends ISocialOffer & { spotlightDates?: { startDate: Date; endDate: Date }; spotlightId?: ObjectId },
>({
  open,
  offer,
  onClose,
  handleRemoveOffer,
  handleRemoveSocialOfferSpotlight,
  onUpdateOffer,
  onUpdateSocialApply,
  openEditModal,
  candidate,
  isApply,
  setSocialApplyOfferFupModal,
  anchor = "right",
}: IStandardDrawerSocialOfferProps<T>) => {
  const { challenges } = useGlobalContext();
  const expired = Boolean(offer.serviceEndDate && offer.serviceEndDate < new Date());

  const header = (
    <DrawerSocialOfferHeader
      offer={offer}
      onClose={onClose}
      handleRemoveOffer={handleRemoveOffer}
      handleRemoveSocialOfferSpotlight={handleRemoveSocialOfferSpotlight}
      onUpdateOffer={onUpdateOffer}
      onUpdateSocialApply={onUpdateSocialApply}
      openEditModal={openEditModal}
      candidate={candidate}
      expired={expired}
      isApply={isApply}
      setSocialApplyOfferFupModal={setSocialApplyOfferFupModal}
    />
  );

  const body = (
    <DrawerSocialOfferBody
      offer={offer}
      challenges={challenges}
      expired={expired}
      candidate={candidate}
      setSocialApplyOfferFupModal={setSocialApplyOfferFupModal}
    />
  );

  return <BaseDrawerSocialOffer open={open} onClose={onClose} header={header} body={body} anchor={anchor} />;
};
